/** Contact phone/email verification UI discriminators and rules. */

import { apiErrorName, isApiClientError } from "@/lib/api";

export const ContactVerifyChannel = {
  Phone: "phone",
  Email: "email",
} as const;

export type ContactVerifyChannel =
  (typeof ContactVerifyChannel)[keyof typeof ContactVerifyChannel];

export const ContactVerifyUiKind = {
  Idle: "idle",
  Sent: "sent",
} as const;

export type ContactVerifyUiKind =
  (typeof ContactVerifyUiKind)[keyof typeof ContactVerifyUiKind];

export interface ContactVerifyState {
  verified: boolean;
  /** Unsaved edit: verification targets the saved value, so save first. */
  dirty: boolean;
  savedValue: string;
  /** The backend rate-limited sends for this channel. */
  sendBlocked: boolean;
}

export function canSendContactVerification(state: ContactVerifyState): boolean {
  return (
    !state.verified &&
    !state.dirty &&
    state.savedValue.trim().length > 0 &&
    !state.sendBlocked
  );
}

/** What sits next to a contact field: a badge, a hint or the send button. */
export type ContactVerifyAction =
  "verified" | "save_first" | "limit_reached" | "send" | "resend";

/** `pending`: a code / link was already sent for this channel. */
export function contactVerifyAction(
  state: ContactVerifyState,
  pending: boolean,
): ContactVerifyAction {
  if (state.verified) return "verified";
  if (state.dirty || !state.savedValue.trim()) return "save_first";
  if (state.sendBlocked) return "limit_reached";
  return pending ? "resend" : "send";
}

function isRateLimited(error: unknown): boolean {
  return isApiClientError(error) && error.response?.status === 429;
}

export type SendVerificationFailure =
  "limit_reached" | "phone_not_sms_capable" | "other";

export function sendVerificationFailure(
  error: unknown,
  channel: ContactVerifyChannel,
): SendVerificationFailure {
  if (isRateLimited(error)) return "limit_reached";
  if (
    channel === ContactVerifyChannel.Phone &&
    apiErrorName(error) === "PhoneNotSmsCapableError"
  ) {
    return "phone_not_sms_capable";
  }
  return "other";
}

export type ConfirmPhoneFailure = "invalid_code" | "limit_reached" | "other";

export function confirmPhoneFailure(error: unknown): ConfirmPhoneFailure {
  if (apiErrorName(error) === "PhoneVerificationFailedError") {
    return "invalid_code";
  }
  if (isRateLimited(error)) return "limit_reached";
  return "other";
}
